/**
 * Timeline Editor Modal (canvas-v2)
 *
 * Full-screen portal with 4-panel CSS grid layout:
 * - ClipPanel (left), VideoPreviewPlayer (center), AdjustPanel (right)
 * - TimelineEditor (bottom)
 *
 * Differences from V1:
 * - 4-panel grid layout (not vertical stack)
 * - Uses executeNode (fire-and-forget via Orchestrator)
 * - Render config includes speed/track_index per clip
 * - Ctrl/Cmd+C/V copy/paste shortcuts
 * - useClipPreloader for blob-based video/audio preloading
 * - useAudioPool for stable multi-track audio playback
 */

import { createPortal, flushSync } from 'react-dom'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNodes } from 'reactflow'
import { TimelineEditor } from './TimelineEditor'
import { VideoPreviewPlayer } from './VideoPreviewPlayer'
import { ClipPanel } from './ClipPanel'
import { AdjustPanel } from './AdjustPanel'
import { useTimelineState } from './useTimelineState'
import { summarizeSelection } from './timelineSelection'
import { useTimelinePlayback } from './useTimelinePlayback'
import { useTimelineYjsSync, writeTimelineDraftToYjs } from './useTimelineYjsSync'
import { useClipPreloader } from './useClipPreloader'
import { useAudioPool } from './useAudioPool'
import {
  buildSelectiveAudioFillClips,
  computeTimelineDuration,
  buildTimelineRenderConfig,
  refreshTimelineAudioClipSourceDuration,
  parseTimelineRenderConfig,
} from './timelineUtils'
import { useYDoc } from '../../../../hooks/useYDoc'
import { executeNode, cancelNodeExecution, apiRequest, createNadDebug } from '@ponpon/api-client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useCanvasOptional } from '../../CanvasContext'
import { emitTimelineNadEvent } from '../../audio-edit/nadTimelineLog'
import {
  ExportConfirmSheet,
  buildDefaultExportName,
} from '../../nodes/VideoEditorNode/ExportConfirmSheet'
import type { ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import { isTimelineRenderConfig, type TimelineClip, type TimelineAudioClip, type TimelineRenderConfig } from './types'

interface TimelineEditorModalProps {
  /** VideoEditorNode ID */
  nodeId: string
  /** Connected video clips */
  videoClips: Array<{ id: string; path: string; name: string; duration?: number }>
  /** Connected audio clips */
  audioClips: Array<{ id: string; path: string; name: string; role?: 'original' | 'replacement' | 'sfx' }>
  /** Node execution status mirrored from YDoc/React Flow */
  nodeStatus?: string
  /** Node progress message mirrored from YDoc/React Flow */
  nodeProgressMessage?: string | null
  /** Close callback */
  onClose: () => void
  /**
   * RFC-0651 #6: open the canvas-level reference selector that the
   * VideoEditorNode toolbar already uses. Plumbed into the ClipPanel as
   * a "+ Add References" button. When undefined, the button is hidden.
   */
  onOpenReferenceSelector?: () => void
}

const timelineLog = createNadDebug('dw:timeline-playback:render-contract')
const selectiveAudioLog = createNadDebug('dw:selective-master-track-audio:gap-fill')
const exportFlowLog = createNadDebug('dw:video-editor:export-flow')
const extractAudioLog = createNadDebug('dw:video-editor:extract-audio')

// RFC-0651 #5 panel-divider clamps (module-level so they can be referenced
// from `useCallback` without becoming a stale-closure trap).
const PANEL_CLAMPS: Record<'left' | 'right' | 'timeline', { min: number; max: number }> = {
  left: { min: 180, max: 400 },
  right: { min: 200, max: 400 },
  timeline: { min: 200, max: 500 },
}

export function TimelineEditorModal({
  nodeId,
  videoClips,
  audioClips: audioClipRefs,
  nodeStatus = 'idle',
  nodeProgressMessage,
  onClose,
  onOpenReferenceSelector,
}: TimelineEditorModalProps) {
  const projectId = useProjectId()
  const canvas = useCanvasOptional()

  const [isRendering, setIsRendering] = useState(false)
  const [renderProgress, setRenderProgress] = useState(0)
  const [renderMessage, setRenderMessage] = useState('')
  const [isCancelling, setIsCancelling] = useState(false)
  const [isExportSheetOpen, setIsExportSheetOpen] = useState(false)
  const [exportSheetDefaultName, setExportSheetDefaultName] = useState(() =>
    buildDefaultExportName(),
  )
  const allCanvasNodes = useNodes<ReactFlowNodeData>()
  const existingLabels = useMemo(() => {
    const labels = new Set<string>()
    for (const node of allCanvasNodes) {
      if (node.id === nodeId) continue
      const rawLabel = node.data?.label
      const label = typeof rawLabel === 'string' && rawLabel ? rawLabel : node.id
      if (label) labels.add(label)
    }
    return labels
  }, [allCanvasNodes, nodeId])
  const isRenderActive = isRendering || nodeStatus === 'running'

  // Convert video clips to TimelineClip format.
  //
  // Per-clip duration source-of-truth ladder:
  //   1. ``clip.duration`` from VideoEditorNode (already populated from
  //      upstream video_generator's ``out_video_duration``). Synchronous,
  //      authoritative, no probe needed.
  //   2. 5s placeholder (preserved for legacy nodes / uploads that don't
  //      expose a duration). The async probe loop further down REPLACES
  //      this with the real measured duration once the file loads — so
  //      this fallback is only visible for ~half a second on first open.
  //
  // Pre-2026-06-08: every clip used the 5s placeholder regardless of
  // upstream metadata, causing the "every clip is 5s in the timeline"
  // bug on monkey-king-fights-with-red-boy-b59a1db4 (after the 10-15s
  // duration clamp landed and clips were genuinely 12-15s).
  const initialClips: TimelineClip[] = useMemo(
    () =>
      videoClips.map((clip, index) => {
        const duration = clip.duration ?? 5
        // Each clip's startOffset is the sum of preceding durations to
        // keep the timeline contiguous from the start.
        const startOffset = videoClips
          .slice(0, index)
          .reduce((acc, prev) => acc + (prev.duration ?? 5), 0)
        return {
          id: clip.id,
          sourceNodeId: clip.id,
          sourcePath: clip.path,
          originalDuration: duration,
          trimStart: 0,
          trimEnd: 0,
          effectiveDuration: duration,
          order: index,
          startOffset,
          trackIndex: 0,
          label: clip.name,
          speed: 1.0,
          volume: 1.0,
          muted: false,
        }
      }),
    [videoClips]
  )

  // Convert audio clips to TimelineAudioClip format
  const initialAudioClips: TimelineAudioClip[] = useMemo(
    () =>
      audioClipRefs.map((af) => ({
        id: af.id,
        sourceNodeId: af.id,
        sourcePath: af.path,
        originalDuration: 15,
        trimStart: 0,
        trimEnd: 0,
        effectiveDuration: 15,
        volume: 0.8,
        muted: false,
        startOffset: 0,
        role: af.role || 'original',
        speed: 1.0,
        trackIndex: 0,
      })),
    [audioClipRefs]
  )

  // Stable keys: only change when clip IDs or paths actually change
  const videoClipsKey = videoClips.map((c) => `${c.id}:${c.path}`).join('|')
  const audioFileKey = audioClipRefs.map((a) => `${a.id}:${a.path}`).join('|')
  const videoClipsRef = useRef(videoClips)
  videoClipsRef.current = videoClips
  const audioClipRefsRef = useRef(audioClipRefs)
  audioClipRefsRef.current = audioClipRefs

  // Timeline state management
  const {
    timelineState,
    addClip,
    addAudioClips,
    setPlayhead,
    setZoom,
    togglePlayPause,
    selectClip,
    toggleClipSelection,
    clearSelection,
    rippleDeleteSelected,
    multiDragOffset,
    reorderClip,
    trimClip,
    trimClipPreview,
    setAudioVolume,
    toggleAudioMute,
    trimAudioClip,
    trimAudioClipPreview,
    setAudioOffset,
    setAudioTrackIndex,
    setClipOffset,
    setClipTrackIndex,
    setClipSpeed,
    setClipVolume,
    setClipFadeOut,
    setClipFadeIn,
    normalizeTimelineOrigin,
    closeAllGaps,
    toggleClipMute,
    setAudioSpeed,
    copyClip,
    pasteClip,
    deleteSelectedClip,
    splitSelectedClip,
    addVideoTrack,
    addAudioTrack,
    removeVideoTrack,
    removeAudioTrack,
    undo,
    redo,
    canUndo,
    canRedo,
    restoreState,
    toggleTrackMute,
    setMagnetEnabled,
    magnetPackTrack0,
    extractAudioFromClip,
  } = useTimelineState()

  // Ref for accessing latest timelineState in async callbacks
  const timelineStateRef = useRef(timelineState)
  timelineStateRef.current = timelineState
  const clipsLength = timelineState.clips.length

  useEffect(() => {
    if (nodeStatus === 'completed' || nodeStatus === 'error') {
      setIsRendering(false)
      setRenderProgress(0)
      if (nodeProgressMessage) {
        setRenderMessage(nodeProgressMessage)
      }
    } else if (nodeStatus === 'running') {
      setIsRendering(true)
      if (nodeProgressMessage) {
        setRenderMessage(nodeProgressMessage)
      }
    }
  }, [nodeStatus, nodeProgressMessage])

  // Collect all unique source paths for preloading.
  // RFC-0631 (Issue 7): also include `timelineState.clips[*].sourcePath`
  // and `timelineState.audioClips[*].sourcePath` so freshly-inserted clips
  // (e.g. the WAV produced by Extract Audio) are preloaded even when they
  // have no upstream edge. Path-keyed Set dedupes against edge-derived
  // entries — no double download.
  const allSourcePaths = useMemo(() => {
    const paths = new Set<string>()
    for (const clip of videoClips) paths.add(clip.path)
    for (const clip of audioClipRefs) paths.add(clip.path)
    for (const clip of timelineState.clips) {
      if (clip.sourcePath) paths.add(clip.sourcePath)
    }
    for (const clip of timelineState.audioClips) {
      if (clip.sourcePath) paths.add(clip.sourcePath)
    }
    return Array.from(paths)
  }, [videoClips, audioClipRefs, timelineState.clips, timelineState.audioClips])

  // Preload all clips as blobs
  const preloader = useClipPreloader({ projectId, sourcePaths: allSourcePaths })

  // Audio pool for stable multi-track playback
  useAudioPool({
    audioClips: timelineState.audioClips,
    playheadPosition: timelineState.playheadPosition,
    isPlaying: timelineState.isPlaying,
    preloader,
    mutedAudioTracks: timelineState.mutedAudioTracks,
  })

  // Load actual video and audio durations from preloader blobs
  const loadedDurationsRef = useRef<Map<string, number>>(new Map())
  const [durationsLoaded, setDurationsLoaded] = useState(false)

  // Reset when source clips/audio change so durations are re-fetched
  const prevKeysRef = useRef(`${videoClipsKey}|${audioFileKey}`)
  useEffect(() => {
    const currentKeys = `${videoClipsKey}|${audioFileKey}`
    if (prevKeysRef.current !== currentKeys) {
      prevKeysRef.current = currentKeys
      loadedDurationsRef.current = new Map()
      setDurationsLoaded(false)
    }
  }, [videoClipsKey, audioFileKey])

  // Probe durations using preloader blob URLs (no redundant downloads)
  useEffect(() => {
    const clips = videoClipsRef.current
    const audioRefs = audioClipRefsRef.current
    if ((clips.length === 0 && audioRefs.length === 0) || durationsLoaded) return

    // Wait for preloader to have all blobs ready
    const allPaths = [...clips.map((c) => c.path), ...audioRefs.map((a) => a.path)]
    const allReady = allPaths.every((p) => preloader.getBlobUrl(p) !== null)
    if (!allReady) return

    let cancelled = false
    const loadDurations = async () => {
      const durations = new Map<string, number>()

      const videoPromises = clips.map(async (clip) => {
        try {
          const blobUrl = preloader.getBlobUrl(clip.path)
          if (!blobUrl) return

          const duration = await new Promise<number>((resolve, reject) => {
            const video = document.createElement('video')
            video.preload = 'metadata'
            video.onloadedmetadata = () => {
              resolve(video.duration)
            }
            video.onerror = () => {
              reject(new Error('Failed to load video metadata'))
            }
            // Use blob URL from preloader — no network fetch needed
            video.src = blobUrl
          })

          durations.set(clip.id, duration)
        } catch (err) {
          console.error(`Failed to load duration for ${clip.name}:`, err)
        }
      })

      const audioPromises = audioRefs.map(async (af) => {
        try {
          const blobUrl = preloader.getBlobUrl(af.path)
          if (!blobUrl) return

          const duration = await new Promise<number>((resolve, reject) => {
            const audio = new Audio()
            audio.preload = 'metadata'
            audio.onloadedmetadata = () => {
              resolve(audio.duration)
            }
            audio.onerror = () => {
              reject(new Error('Failed to load audio metadata'))
            }
            // Use blob URL from preloader — no network fetch needed
            audio.src = blobUrl
          })

          durations.set(af.id, duration)
        } catch (err) {
          console.error(`Failed to load audio duration for ${af.name}:`, err)
        }
      })

      await Promise.all([...videoPromises, ...audioPromises])

      if (!cancelled) {
        loadedDurationsRef.current = durations
        setDurationsLoaded(true)
      }
    }

    loadDurations()
    return () => {
      cancelled = true
    }
  }, [durationsLoaded, preloader])

  // Fingerprint that changes when any clip has a stale/placeholder duration
  const clipDurationFingerprint = timelineState.clips
    .map((c) => `${c.id}:${c.originalDuration}`)
    .join(',')

  // Apply loaded durations to video and audio clips once both are ready
  useEffect(() => {
    if (!durationsLoaded || clipsLength === 0) return
    const durations = loadedDurationsRef.current
    if (durations.size === 0) return

    const currentState = timelineStateRef.current
    const currentClips = currentState.clips
    const currentAudioClips = currentState.audioClips

    const videoNeedsUpdate = currentClips.some((clip) => {
      const realDuration = durations.get(clip.sourceNodeId) ?? durations.get(clip.id)
      return realDuration && realDuration !== clip.originalDuration
    })
    const audioNeedsUpdate = currentAudioClips.some((clip) => {
      const realDuration = durations.get(clip.sourceNodeId) ?? durations.get(clip.id)
      return realDuration && realDuration !== clip.originalDuration
    })

    if (videoNeedsUpdate || audioNeedsUpdate) {
      const updatedClips = currentClips.map((clip) => {
        const realDuration = durations.get(clip.sourceNodeId) ?? durations.get(clip.id)
        if (realDuration && realDuration !== clip.originalDuration) {
          return {
            ...clip,
            originalDuration: realDuration,
            effectiveDuration: realDuration - clip.trimStart - clip.trimEnd,
          }
        }
        return clip
      })

      const updatedAudioClips = currentAudioClips.map((clip) => {
        const realDuration = durations.get(clip.sourceNodeId) ?? durations.get(clip.id)
        if (realDuration && realDuration !== clip.originalDuration) {
          return refreshTimelineAudioClipSourceDuration(clip, realDuration)
        }
        return clip
      })

      const totalDuration = computeTimelineDuration(updatedClips, updatedAudioClips)
      restoreState({
        ...currentState,
        clips: updatedClips,
        audioClips: updatedAudioClips,
        totalDuration,
      })
    }
  }, [durationsLoaded, clipsLength, clipDurationFingerprint, restoreState])

  // Yjs document connection for draft sync
  const { ydoc, synced } = useYDoc({
    projectId: projectId || '',
    canvasId: 'agent',
    enabled: !!projectId,
  })

  // Compute valid source IDs from edge-connected clips for Yjs filtering.
  // This prevents stale (disconnected) clips from flashing on modal open.
  const validVideoSourceIds = useMemo(
    () => new Set(videoClips.map(c => c.id)),
    [videoClips]
  )
  const validAudioSourceIds = useMemo(
    () => new Set(audioClipRefs.map(c => c.id)),
    [audioClipRefs]
  )

  // Sync timeline state to Yjs with debouncing
  useTimelineYjsSync({
    ydoc,
    synced,
    timelineState,
    nodeId,
    onRestoreState: restoreState,
    debounceMs: 2000,
    validVideoSourceIds,
    validAudioSourceIds,
  })

  // Extract in_timeline_config from the node's ReactFlow data so the init
  // effect can use it without depending on the full allCanvasNodes array.
  const savedTimelineConfig = useMemo((): TimelineRenderConfig | undefined => {
    const raw = allCanvasNodes.find(n => n.id === nodeId)?.data?.in_timeline_config
    if (!isTimelineRenderConfig(raw)) return undefined
    return raw
  }, [allCanvasNodes, nodeId])

  // Initialize clips on mount, or merge new clips from edges into Yjs-restored state
  useEffect(() => {
    if (initialClips.length === 0) return

    if (timelineState.clips.length === 0) {
      // Fresh start — no Yjs draft. If in_timeline_config exists (e.g. written
      // by the Director), parse it so the editor reflects the agent's config.
      if (savedTimelineConfig) {
        restoreState(parseTimelineRenderConfig(savedTimelineConfig, initialClips, initialAudioClips))
      } else {
        const totalDuration = computeTimelineDuration(initialClips, initialAudioClips)
        restoreState({
          clips: initialClips,
          audioClips: initialAudioClips,
          playheadPosition: 0,
          zoomLevel: 50,
          isPlaying: false,
          selectedClipIds: new Set<string>(),
          totalDuration,
          videoTrackCount: 1,
          audioTrackCount: 1,
          copiedClip: null,
          copiedClipType: null,
          mutedVideoTracks: new Set<number>(),
          mutedAudioTracks: new Set<number>(),
          magnetEnabled: true,
        })
      }
    } else {
      // Merge: add new clips from edges AND prune stale clips no longer edge-connected
      const currentState = timelineStateRef.current
      const initialClipsBySourceId = new Map(initialClips.map(c => [c.sourceNodeId, c]))
      const initialSourceIds = new Set(initialClips.map(c => c.sourceNodeId))
      const existingSourceIds = new Set(currentState.clips.map(c => c.sourceNodeId))
      const newClips = initialClips.filter(c => !existingSourceIds.has(c.sourceNodeId))

      // Prune disconnected clips and refresh canonical asset refs from edge-resolved inputs.
      const prunedClips = currentState.clips
        .filter(c => initialSourceIds.has(c.sourceNodeId))
        .map(c => {
          const currentRef = initialClipsBySourceId.get(c.sourceNodeId)
          return currentRef
            ? { ...c, sourcePath: currentRef.sourcePath, label: currentRef.label }
            : c
        })

      const maxOrder = prunedClips.reduce((max, c) => Math.max(max, c.order), -1)
      const nextClipStart = computeTimelineDuration(prunedClips, currentState.audioClips)
      const mergedClips = [
        ...prunedClips,
        ...newClips.map((clip, idx) => ({
          ...clip,
          order: maxOrder + 1 + idx,
          startOffset: nextClipStart + idx * clip.effectiveDuration,
          trackIndex: 0,
        })),
      ]
      const videoRefsChanged = prunedClips.some((clip) => {
        const previous = currentState.clips.find(c => c.sourceNodeId === clip.sourceNodeId)
        return previous?.sourcePath !== clip.sourcePath || previous?.label !== clip.label
      })

      // Also merge + prune audio clips
      const initialAudioClipsBySourceId = new Map(initialAudioClips.map(c => [c.sourceNodeId, c]))
      const initialAudioSourceIds = new Set(initialAudioClips.map(c => c.sourceNodeId))
      const existingAudioSourceIds = new Set(currentState.audioClips.map(c => c.sourceNodeId))
      const newAudioClipRefs = initialAudioClips.filter(c => !existingAudioSourceIds.has(c.sourceNodeId))
      const prunedAudioClips = currentState.audioClips
        .filter(c => initialAudioSourceIds.has(c.sourceNodeId))
        .map(c => {
          const currentRef = initialAudioClipsBySourceId.get(c.sourceNodeId)
          return currentRef
            ? { ...c, sourcePath: currentRef.sourcePath }
            : c
        })

      const videoTimelineEndS = computeTimelineDuration(mergedClips, [])
      let accumulatedAudioClips = [...prunedAudioClips]
      const newAudioClips = newAudioClipRefs.flatMap((clip) => {
        const duration = loadedDurationsRef.current.get(clip.sourceNodeId) ?? clip.originalDuration
        const timelineEndS = videoTimelineEndS > 0
          ? videoTimelineEndS
          : Math.max(currentState.totalDuration, clip.startOffset + duration)
        const fillClips = buildSelectiveAudioFillClips({
          sourceNodeId: clip.sourceNodeId,
          sourcePath: clip.sourcePath,
          originalDuration: duration,
          role: clip.role,
          volume: clip.volume,
          muted: clip.muted,
          startOffset: clip.startOffset,
          trackIndex: clip.trackIndex,
          timelineEndS,
          existingAudioClips: accumulatedAudioClips,
        })
        const logPayload = {
          event: fillClips.length > 0 ? 'selective-audio-fill-created' : 'selective-audio-fill-empty',
          trace_id: `selective-master-track-audio:${nodeId}:${clip.sourceNodeId}`,
          source: 'edge-merge',
          nodeId,
          sourceNodeId: clip.sourceNodeId,
          sourcePath: clip.sourcePath,
          sourceDurationS: Number(duration.toFixed(3)),
          startOffsetS: Number(clip.startOffset.toFixed(3)),
          timelineEndS: Number(timelineEndS.toFixed(3)),
          existingAudioClipCount: accumulatedAudioClips.length,
          existingUnmutedAudioClipCount: accumulatedAudioClips.filter(
            (existingClip) => !existingClip.muted,
          ).length,
          createdClipCount: fillClips.length,
          createdRanges: fillClips.map((createdClip) => ({
            startOffsetS: Number(createdClip.startOffset.toFixed(3)),
            durationS: Number(createdClip.effectiveDuration.toFixed(3)),
            sourceInS: Number(createdClip.trimStart.toFixed(3)),
          })),
        }
        selectiveAudioLog(logPayload)
        emitTimelineNadEvent('dw:selective-master-track-audio:gap-fill', logPayload)
        accumulatedAudioClips = [...accumulatedAudioClips, ...fillClips]
        return fillClips
      })
      const audioRefsChanged = prunedAudioClips.some((clip) => {
        const previous = currentState.audioClips.find(c => c.sourceNodeId === clip.sourceNodeId)
        return previous?.sourcePath !== clip.sourcePath
      })

      if (newClips.length > 0 || prunedClips.length !== currentState.clips.length ||
          videoRefsChanged || newAudioClips.length > 0 ||
          prunedAudioClips.length !== currentState.audioClips.length || audioRefsChanged) {
        const nextAudioClips = [...prunedAudioClips, ...newAudioClips]
        restoreState({
          ...currentState,
          clips: mergedClips,
          audioClips: nextAudioClips,
          totalDuration: computeTimelineDuration(mergedClips, nextAudioClips),
          videoTrackCount: Math.max(currentState.videoTrackCount, ...mergedClips.map((clip) => clip.trackIndex + 1), 1),
          audioTrackCount: Math.max(currentState.audioTrackCount, ...nextAudioClips.map((clip) => clip.trackIndex + 1), 1),
        })
      }
    }
  }, [
    initialClips,
    initialAudioClips,
    nodeId,
    savedTimelineConfig,
    timelineState.clips.length,
    timelineState.audioClips.length,
    restoreState,
  ])

  // RFC-0633 round-4 (Issue 5): each `TimelineClip` owns its own per-source
  // thumbnail cache via `useClipThumbnails`. The hook resolves asset URLs
  // internally via `useProjectId` + `fetchMediaBlob`, so there is no
  // pre-resolved URL projection or batch lift here any more.

  // Playback hook
  const {
    togglePlayPause: handleTogglePlayPause,
    scrub,
    jumpToStart,
    jumpToEnd,
    playheadPositionRef,
  } = useTimelinePlayback({
    timelineState,
    setPlayhead,
    togglePlayPause,
  })

  // Selection summary — derived projection of the multi-selection set used by
  // the legacy single-selection consumers (AdjustPanel, ClipPanel highlight).
  // For multi-selection these consumers gracefully fall back to a "select one
  // clip to adjust" affordance.
  const selectionSummary = useMemo(() => summarizeSelection(timelineState), [timelineState])
  const isMultiSelected = timelineState.selectedClipIds.size > 1

  // Selected clip objects for AdjustPanel — only meaningful when exactly one
  // clip is selected.
  const selectedVideoClip = useMemo(
    () =>
      selectionSummary.type === 'video' && !isMultiSelected
        ? timelineState.clips.find((c) => c.id === selectionSummary.primaryId) ?? null
        : null,
    [timelineState.clips, selectionSummary, isMultiSelected]
  )

  const selectedAudioClip = useMemo(
    () =>
      selectionSummary.type === 'audio' && !isMultiSelected
        ? timelineState.audioClips.find((c) => c.id === selectionSummary.primaryId) ?? null
        : null,
    [timelineState.audioClips, selectionSummary, isMultiSelected]
  )

  // Route speed changes to correct handler based on clip type
  const handleSpeedChange = useCallback(
    (clipId: string, speed: number) => {
      const isAudio = timelineState.audioClips.some((c) => c.id === clipId)
      if (isAudio) {
        setAudioSpeed(clipId, speed)
      } else {
        setClipSpeed(clipId, speed)
      }
    },
    [timelineState.audioClips, setAudioSpeed, setClipSpeed]
  )

  // ScrollToClip handler — selects the clip (which highlights it in the timeline)
  const handleScrollToClip = useCallback(
    (clipId: string, clipType: 'video' | 'audio') => {
      selectClip(clipId, clipType)
    },
    [selectClip]
  )

  // Add a reference clip instance to the timeline.
  const addReferenceClipInstance = useCallback(
    (clipId: string, clipType: 'video' | 'audio', startOffset: number, trackIndex: number) => {
      timelineLog({
        event: 'reference-instance-add',
        nodeId,
        clipId,
        clipType,
        startOffset,
        trackIndex,
      })
      if (clipType === 'video') {
        const ref = videoClips.find(c => c.id === clipId)
        if (!ref) return
        const duration = loadedDurationsRef.current.get(ref.id) ?? 5
        addClip({
          id: crypto.randomUUID(),
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          originalDuration: duration,
          trimStart: 0,
          trimEnd: 0,
          effectiveDuration: duration,
          order: timelineStateRef.current.clips.length,
          startOffset: Math.max(0, startOffset),
          trackIndex: Math.max(0, Math.min(timelineStateRef.current.videoTrackCount - 1, trackIndex)),
          label: ref.name,
          speed: 1.0,
          volume: 1.0,
          muted: false,
        })
      } else {
        const ref = audioClipRefs.find(c => c.id === clipId)
        if (!ref) return
        const duration = loadedDurationsRef.current.get(ref.id) ?? 15
        const videoTimelineEndS = computeTimelineDuration(timelineStateRef.current.clips, [])
        const timelineEndS = videoTimelineEndS > 0
          ? videoTimelineEndS
          : Math.max(timelineStateRef.current.totalDuration, startOffset + duration)
        const fillClips = buildSelectiveAudioFillClips({
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          originalDuration: duration,
          role: ref.role || 'original',
          volume: 0.8,
          startOffset: Math.max(0, startOffset),
          trackIndex: Math.max(0, Math.min(timelineStateRef.current.audioTrackCount - 1, trackIndex)),
          timelineEndS,
          existingAudioClips: timelineStateRef.current.audioClips,
        })
        const logPayload = {
          event: fillClips.length > 0 ? 'selective-audio-fill-created' : 'selective-audio-fill-empty',
          trace_id: `selective-master-track-audio:${nodeId}:${ref.id}`,
          source: 'reference-add',
          nodeId,
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          sourceDurationS: Number(duration.toFixed(3)),
          startOffsetS: Number(Math.max(0, startOffset).toFixed(3)),
          timelineEndS: Number(timelineEndS.toFixed(3)),
          existingAudioClipCount: timelineStateRef.current.audioClips.length,
          existingUnmutedAudioClipCount: timelineStateRef.current.audioClips.filter(
            (clip) => !clip.muted,
          ).length,
          createdClipCount: fillClips.length,
          createdRanges: fillClips.map((clip) => ({
            startOffsetS: Number(clip.startOffset.toFixed(3)),
            durationS: Number(clip.effectiveDuration.toFixed(3)),
            sourceInS: Number(clip.trimStart.toFixed(3)),
          })),
        }
        selectiveAudioLog(logPayload)
        emitTimelineNadEvent('dw:selective-master-track-audio:gap-fill', logPayload)
        if (fillClips.length > 0) {
          addAudioClips(fillClips)
        }
      }
    },
    [nodeId, videoClips, audioClipRefs, addClip, addAudioClips]
  )

  const handleAddClipToTimeline = useCallback(
    (clipId: string, clipType: 'video' | 'audio') => {
      addReferenceClipInstance(clipId, clipType, timelineStateRef.current.playheadPosition, 0)
    },
    [addReferenceClipInstance]
  )

  const handleAddReferenceAt = useCallback(
    (clipId: string, clipType: 'video' | 'audio', startOffset: number, trackIndex: number) => {
      addReferenceClipInstance(clipId, clipType, startOffset, trackIndex)
    },
    [addReferenceClipInstance]
  )

  // Save timeline config to YDoc on close (when not rendering)
  const handleClose = useCallback(() => {
    if (!isRenderActive && timelineState.clips.length > 0 && canvas) {
      const draftWritten = writeTimelineDraftToYjs(ydoc, nodeId, timelineState)
      timelineLog({
        event: 'draft-flushed-before-close',
        nodeId,
        clipCount: timelineState.clips.length,
        audioClipCount: timelineState.audioClips.length,
        written: draftWritten,
      })
      timelineLog({ event: 'save-on-close', nodeId, clipCount: timelineState.clips.length })
      const renderConfig = buildTimelineRenderConfig(timelineState)
      canvas.updateNodeData(nodeId, { in_timeline_config: renderConfig })
    }
    onClose()
  }, [isRenderActive, timelineState, canvas, ydoc, nodeId, onClose])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // The bailout used to skip the entire shortcut handler for any focused
      // <input>/<textarea>, which meant a focused volume slider (type="range")
      // would silently swallow Space and nudge its value instead of toggling
      // playback.
      //
      // The fix lets Space through for non-text inputs (range/checkbox/radio
      // and non-input targets), while keeping the bailout for text-entry
      // inputs (text/email/textarea/etc.) so users can type spaces in the
      // export-name dialog without toggling playback. All OTHER shortcuts
      // (Arrow scrub, Home/End jump, Delete, Ctrl/Cmd+Z, etc.) still bail
      // for ANY focused input so the input's native keyboard behaviour
      // (Arrow nudges value, Home/End jumps caret/value, etc.) is preserved.
      const target = e.target
      const isInputLike =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
      const isTextInput =
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLInputElement &&
          target.type !== 'range' &&
          target.type !== 'checkbox' &&
          target.type !== 'radio' &&
          target.type !== 'button' &&
          target.type !== 'submit')
      if (e.code === 'Space') {
        // Text inputs always win Space (typing). Everything else (range, body,
        // buttons) routes Space to the playback toggle.
        if (isTextInput) return
        timelineLog({
          event: 'space-keydown',
          nodeId,
          targetTag: target instanceof HTMLElement ? target.tagName : 'unknown',
          targetType: target instanceof HTMLInputElement ? target.type : null,
          isInputLike,
          isTextInput,
          willToggle: true,
        })
      } else if (isInputLike) {
        return
      }

      // Escape: Close modal (blocked during render)
      if (e.code === 'Escape') {
        e.preventDefault()
        e.stopImmediatePropagation()
        handleClose()
        return
      }

      // Space: Play/Pause. If the playhead is at (or near) the end of the
      // timeline AND we're about to play, reset to 0 first so users can
      // re-watch without manually scrubbing back. Epsilon = 0.05s.
      if (e.code === 'Space') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const playhead = timelineState.playheadPosition
        const total = timelineState.totalDuration
        if (
          !timelineState.isPlaying &&
          total > 0 &&
          playhead >= total - 0.05
        ) {
          timelineLog({
            event: 'spacebar-reset-at-end',
            nodeId,
            wasS: Number(playhead.toFixed(3)),
            totalS: Number(total.toFixed(3)),
          })
          // Reset playhead first; togglePlayPause then transitions to PLAY.
          setPlayhead(0)
          if (playheadPositionRef) {
            playheadPositionRef.current = 0
          }
        }
        handleTogglePlayPause()
        return
      }

      // Zoom keys: '-' / '+' / '=' (matches toolbar +/- buttons, ±10 step)
      if (e.code === 'Minus' || e.code === 'NumpadSubtract') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const next = Math.max(10, Math.min(200, timelineState.zoomLevel - 10))
        timelineLog({
          event: 'zoom-key',
          nodeId,
          direction: 'out',
          delta: -10,
          fromZoom: timelineState.zoomLevel,
          toZoom: next,
        })
        setZoom(next)
        return
      }
      if (e.code === 'Equal' || e.code === 'NumpadAdd') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const next = Math.max(10, Math.min(200, timelineState.zoomLevel + 10))
        timelineLog({
          event: 'zoom-key',
          nodeId,
          direction: 'in',
          delta: 10,
          fromZoom: timelineState.zoomLevel,
          toZoom: next,
        })
        setZoom(next)
        return
      }

      // S: Split selected clip at playhead (type-aware)
      if (e.code === 'KeyS' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        splitSelectedClip()
        return
      }

      // Delete/Backspace: Remove selected clips (multi-aware)
      // Shift+Delete or Shift+Backspace → Ripple delete (Premiere-style)
      if ((e.code === 'Delete' || e.code === 'Backspace') && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (e.shiftKey) {
          rippleDeleteSelected()
        } else {
          deleteSelectedClip()
        }
        return
      }

      // Ctrl/Cmd+Z: Undo
      if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (canUndo) {
          undo()
        }
        return
      }

      // Ctrl/Cmd+Shift+Z: Redo
      if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (canRedo) {
          redo()
        }
        return
      }

      // Ctrl/Cmd+C: Copy selected clip
      if (e.code === 'KeyC' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        copyClip()
        return
      }

      // Ctrl/Cmd+V: Paste copied clip
      if (e.code === 'KeyV' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        pasteClip()
        return
      }

      // Left Arrow: Move playhead back 1 second
      if (e.code === 'ArrowLeft' && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(-1)
        return
      }

      // Right Arrow: Move playhead forward 1 second
      if (e.code === 'ArrowRight' && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(1)
        return
      }

      // Shift+Left: Fine scrub back 0.1 second
      if (e.code === 'ArrowLeft' && e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(-0.1)
        return
      }

      // Shift+Right: Fine scrub forward 0.1 second
      if (e.code === 'ArrowRight' && e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(0.1)
        return
      }

      // Home: Jump to start
      if (e.code === 'Home') {
        e.preventDefault()
        e.stopImmediatePropagation()
        jumpToStart()
        return
      }

      // End: Jump to end
      if (e.code === 'End') {
        e.preventDefault()
        e.stopImmediatePropagation()
        jumpToEnd()
        return
      }
    }

    // Use capture phase on document so we fire BEFORE ReactFlow's bubble-phase
    // keydown listener on document. This prevents Delete/Backspace from deleting
    // the canvas node while the timeline editor modal is open.
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [
    handleTogglePlayPause,
    splitSelectedClip,
    deleteSelectedClip,
    rippleDeleteSelected,
    canUndo,
    canRedo,
    undo,
    redo,
    copyClip,
    pasteClip,
    scrub,
    jumpToStart,
    jumpToEnd,
    handleClose,
    nodeId,
    setPlayhead,
    setZoom,
    timelineState.playheadPosition,
    timelineState.totalDuration,
    timelineState.isPlaying,
    timelineState.zoomLevel,
    playheadPositionRef,
  ])

  // Open the Export confirm sheet (replaces direct render dispatch).
  const handleOpenExportSheet = useCallback(() => {
    if (!projectId) return
    if (timelineState.clips.length === 0) return
    const defaultName = buildDefaultExportName()
    setExportSheetDefaultName(defaultName)
    setIsExportSheetOpen(true)
    exportFlowLog({
      event: 'open-export-sheet',
      origin: 'timeline-modal',
      nodeId,
      defaultName,
      existingLabelsCount: existingLabels.size,
    })
  }, [projectId, nodeId, timelineState.clips.length, existingLabels])

  // Cancel the confirm sheet.
  const handleCancelExportSheet = useCallback(() => {
    setIsExportSheetOpen(false)
    exportFlowLog({ event: 'cancel-export-sheet', origin: 'timeline-modal', nodeId })
  }, [nodeId])

  // Confirm sheet → write the chosen name and dispatch the render.
  const handleConfirmExport = useCallback(
    async (chosenName: string) => {
      if (!projectId) return
      if (timelineState.clips.length === 0) return
      setIsExportSheetOpen(false)

      flushSync(() => {
        setIsRendering(true)
        setRenderProgress(0)
        setRenderMessage('Rendering in the background. It is safe to go back to canvas.')
      })

      try {
        const renderConfig = buildTimelineRenderConfig(timelineState)
        const draftWritten = writeTimelineDraftToYjs(ydoc, nodeId, timelineState)
        timelineLog({
          event: 'draft-flushed-before-export',
          nodeId,
          clipCount: timelineState.clips.length,
          audioClipCount: timelineState.audioClips.length,
          written: draftWritten,
        })

        const renderLogPayload = {
          event: 'render-requested',
          nodeId,
          chosenName,
          clipCount: timelineState.clips.length,
          audioClipCount: timelineState.audioClips.length,
          totalDuration: timelineState.totalDuration,
          videoClips: timelineState.clips.map((clip) => ({
            clipId: clip.id,
            sourcePath: clip.sourcePath,
            startTimeS: Number(clip.trimStart.toFixed(3)),
            endTimeS: Number((clip.originalDuration - clip.trimEnd).toFixed(3)),
            startOffsetS: Number(clip.startOffset.toFixed(3)),
            speed: Number(clip.speed.toFixed(3)),
            trackIndex: clip.trackIndex,
            volume: Number(clip.volume.toFixed(3)),
            muted: clip.muted,
          })),
          audioTracks: timelineState.audioClips
            .filter((clip) => !clip.muted)
            .map((clip) => ({
              clipId: clip.id,
              startTimeS: Number(clip.trimStart.toFixed(3)),
              endTimeS: Number((clip.originalDuration - clip.trimEnd).toFixed(3)),
              startOffsetS: Number(clip.startOffset.toFixed(3)),
              speed: Number(clip.speed.toFixed(3)),
              volume: Number(clip.volume.toFixed(3)),
            })),
        }
        timelineLog(renderLogPayload)
        emitTimelineNadEvent('dw:timeline-playback:render-contract', renderLogPayload)
        exportFlowLog({ event: 'confirm-export', origin: 'timeline-modal', nodeId, chosenName })

        // Write timeline config + chosen name to YDoc before executing.
        canvas?.updateNodeData(nodeId, {
          in_timeline_config: renderConfig,
          in_export_name: chosenName,
          progressMessage: 'Render started - processing on server...',
        })
        timelineLog({
          event: 'execute-with-fresh-inputs',
          nodeId,
          hasInputUpdates: true,
          clipCount: timelineState.clips.length,
          audioClipCount: timelineState.audioClips.length,
        })
        await executeNode(projectId, nodeId, 'timeline_render', undefined, {
          in_timeline_config: renderConfig,
          in_export_name: chosenName,
        })

        // executeNode returns immediately. Backend updates node status via Yjs.
        setRenderMessage('Render started - processing on server...')
      } catch (err) {
        console.error('Render failed:', err)
        setIsRendering(false)
        setRenderProgress(0)
        setRenderMessage('Render failed')
      }
    },
    [projectId, nodeId, timelineState, canvas, ydoc],
  )

  // Cancel ongoing render
  const handleCancelRender = useCallback(async () => {
    if (!projectId || isCancelling) return
    timelineLog({ event: 'modal-cancel-requested', nodeId })
    setIsCancelling(true)
    try {
      await cancelNodeExecution(projectId, nodeId)
      timelineLog({ event: 'modal-cancel-dispatched', nodeId })
    } catch (err) {
      timelineLog({ event: 'modal-cancel-failed', nodeId, error: String(err) })
      console.error('Cancel render failed:', err)
    } finally {
      setIsCancelling(false)
    }
  }, [projectId, nodeId, isCancelling])

  // RFC-0651 #3: when magnet is on, schedule a track-0 pack on the next
  // microtask after any commit that may have affected track 0. We use
  // requestAnimationFrame so the reducer's history push completes first.
  const magnetEnabledRef = useRef(timelineState.magnetEnabled)
  magnetEnabledRef.current = timelineState.magnetEnabled
  const schedulePack = useCallback(() => {
    if (!magnetEnabledRef.current) return
    requestAnimationFrame(() => {
      if (!magnetEnabledRef.current) return
      magnetPackTrack0()
    })
  }, [magnetPackTrack0])

  const handleTrimClipCommit = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      trimClip(clipId, trimStart, trimEnd, startOffset)
      const target = timelineStateRef.current.clips.find((c) => c.id === clipId)
      if (target?.trackIndex === 0) schedulePack()
    },
    [trimClip, schedulePack],
  )

  const handleMultiDragCommit = useCallback(
    (deltaSec: number, trackDelta?: number) => {
      multiDragOffset(deltaSec, trackDelta)
      const touchesTrack0 = Array.from(timelineStateRef.current.selectedClipIds).some(
        (id) => timelineStateRef.current.clips.find((c) => c.id === id)?.trackIndex === 0,
      )
      if (touchesTrack0) schedulePack()
    },
    [multiDragOffset, schedulePack],
  )

  const handleSetClipOffsetCommit = useCallback(
    (clipId: string, startOffset: number) => {
      setClipOffset(clipId, startOffset)
      const target = timelineStateRef.current.clips.find((c) => c.id === clipId)
      if (target?.trackIndex === 0) schedulePack()
    },
    [setClipOffset, schedulePack],
  )

  const handleSplitSelected = useCallback(() => {
    splitSelectedClip()
    schedulePack()
  }, [splitSelectedClip, schedulePack])

  // RFC-0630 v3 (architect-review #2): reorder release is one of the six
  // commit points the plan listed for magnet-pack. Reorder may move a clip
  // between tracks; pack if either the source's *old* trackIndex was 0 OR
  // the clip after the move sits on track 0. The reorder reducer doesn't
  // change trackIndex, so checking the current trackIndex is sufficient.
  const handleReorderClipCommit = useCallback(
    (clipId: string, newOrder: number) => {
      reorderClip(clipId, newOrder)
      const target = timelineStateRef.current.clips.find((c) => c.id === clipId)
      if (target?.trackIndex === 0) schedulePack()
    },
    [reorderClip, schedulePack],
  )

  // RFC-0651 #12: extract-audio handler. Decodes the video clip's audio
  // track via Web Audio, encodes as WAV, uploads through the project asset
  // endpoint, then dispatches EXTRACT_AUDIO_FROM_CLIP to insert a new
  // audio track directly under the source video track.
  const handleExtractAudio = useCallback(
    async (videoClip: TimelineClip) => {
      try {
        if (!projectId) throw new Error('No project id')
        const blobUrl = preloader.getBlobUrl(videoClip.sourcePath)
        if (!blobUrl) throw new Error('Source blob unavailable')
        const blobRes = await fetch(blobUrl)
        const blob = await blobRes.blob()
        const arrayBuffer = await blob.arrayBuffer()
        const AudioContextCtor = window.AudioContext || window.webkitAudioContext
        const ctx = new AudioContextCtor()
        const decoded = await ctx.decodeAudioData(arrayBuffer.slice(0))
        const startSec = videoClip.trimStart
        const endSec = startSec + videoClip.effectiveDuration * videoClip.speed
        const startSample = Math.floor(startSec * decoded.sampleRate)
        const endSample = Math.min(decoded.length, Math.floor(endSec * decoded.sampleRate))
        const sliceLength = Math.max(0, endSample - startSample)
        if (sliceLength <= 0) throw new Error('Empty audio slice')
        const channelCount = Math.min(2, decoded.numberOfChannels)
        const channels: Float32Array[] = []
        for (let ch = 0; ch < channelCount; ch += 1) {
          const src = decoded.getChannelData(ch)
          channels.push(src.slice(startSample, endSample))
        }
        const wavBlob = encodeWavBlob(channels, decoded.sampleRate)
        void ctx.close()

        const formData = new FormData()
        const filename = `extracted-${videoClip.id.slice(0, 8)}.wav`
        formData.append('file', wavBlob, filename)
        formData.append('name', filename)
        // RFC-0630 v3 (architect-review #1): the correct upload endpoint is
        // `/projects/:id/assets/upload`. Using the canonical `apiRequest`
        // helper (same one client.uploadAsset uses) also takes care of the
        // `/api` prefix, `credentials: 'include'`, and 4xx → Error mapping.
        let uploadJsonRaw: AssetUploadResponse
        try {
          uploadJsonRaw = await apiRequest<AssetUploadResponse>(
            `/projects/${projectId}/assets/upload`,
            { method: 'POST', body: formData },
          )
        } catch (uploadErr) {
          extractAudioLog({
            event: 'extract-audio-upload-failed',
            clipId: videoClip.id,
            error: uploadErr instanceof Error ? uploadErr.message : String(uploadErr),
          })
          throw uploadErr
        }
        const relpath = uploadJsonRaw.path ?? uploadJsonRaw.relpath
        if (!relpath) throw new Error('asset upload response missing path')

        // RFC-0630 v3 (architect-review low item): place the new audio track
        // **immediately below the source clip's video track**. The audio
        // and video track stacks are independent, but matching the source
        // video's trackIndex keeps adjacent tracks visually aligned in the
        // common case (single video stack at index 0). The reducer shifts
        // any existing audio clips ≥ newAudioTrackIndex down by 1.
        const newAudioTrackIndex = Math.max(0, videoClip.trackIndex)
        const newAudioClip: TimelineAudioClip = {
          id: `extracted-${crypto.randomUUID()}`,
          sourceNodeId: videoClip.sourceNodeId,
          sourcePath: relpath,
          originalDuration: sliceLength / decoded.sampleRate,
          trimStart: 0,
          trimEnd: 0,
          effectiveDuration: sliceLength / decoded.sampleRate,
          volume: 1,
          muted: false,
          startOffset: videoClip.startOffset,
          role: 'original',
          speed: 1,
          trackIndex: newAudioTrackIndex,
        }
        extractAudioFromClip(videoClip.id, newAudioClip, newAudioTrackIndex)
        // RFC-0631 (Issue 7): emit a log right after dispatch so the audio
        // preloader's behaviour can be cross-checked against the timeline
        // state transition. If the new clip's sourcePath never appears in a
        // `useClipPreloader` log, we know the input set is stale.
        extractAudioLog({
          event: 'extract-audio-await-preload',
          clipId: newAudioClip.id,
          sourcePath: newAudioClip.sourcePath,
          sourceClipId: videoClip.id,
        })
        // RFC-0631 (Issue 6): auto-mute the source video clip so the user
        // doesn't hear the embedded audio doubled up with the extracted
        // track. Skip if already muted.
        const alreadyMuted = videoClip.muted ?? false
        if (!alreadyMuted) {
          toggleClipMute(videoClip.id)
        }
        extractAudioLog({
          event: 'auto-mute-source',
          sourceClipId: videoClip.id,
          alreadyMuted,
        })
      } catch (err) {
        console.error('[VideoEditor] extract-audio failed', err)
      }
    },
    [projectId, preloader, extractAudioFromClip, toggleClipMute],
  )

  // RFC-0651 #5: resizable panel dividers (left-rail/preview,
  // preview/right-rail, preview/timeline). Sizes persist to localStorage
  // under `videoEditor:panel-sizes`. Clamps:
  //   leftWidth     180–400 px
  //   rightWidth    200–400 px
  //   timelineHeight 200–500 px
  const PANEL_SIZES_LS_KEY = 'videoEditor:panel-sizes'
  // Module-level constants (not state) so they don't need to be deps of
  // the splitter handler — `useCallback` only watches the dynamic inputs.
  const PANEL_DEFAULTS = { left: 250, right: 280, timeline: 300 }
  const layoutLog = useMemo(() => createNadDebug('dw:video-editor:layout'), [])
  const [panelSizes, setPanelSizes] = useState(() => {
    try {
      const raw = localStorage.getItem(PANEL_SIZES_LS_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        const clamp = (val: number, range: { min: number; max: number }) =>
          Math.max(range.min, Math.min(range.max, val))
        return {
          left: clamp(
            typeof parsed.left === 'number' ? parsed.left : PANEL_DEFAULTS.left,
            PANEL_CLAMPS.left,
          ),
          right: clamp(
            typeof parsed.right === 'number' ? parsed.right : PANEL_DEFAULTS.right,
            PANEL_CLAMPS.right,
          ),
          timeline: clamp(
            typeof parsed.timeline === 'number' ? parsed.timeline : PANEL_DEFAULTS.timeline,
            PANEL_CLAMPS.timeline,
          ),
        }
      }
    } catch (err) {
      console.warn('[videoEditor] failed to read panel sizes from localStorage', err)
    }
    return PANEL_DEFAULTS
  })

  // Persist panel sizes (debounced via a microtask).
  useEffect(() => {
    try {
      localStorage.setItem(PANEL_SIZES_LS_KEY, JSON.stringify(panelSizes))
    } catch (err) {
      console.warn('[videoEditor] failed to persist panel sizes', err)
    }
  }, [panelSizes])

  // Splitter drag handler factory. `dimension` selects which size we adjust
  // and `axis` selects whether we read deltaX or deltaY. We track the start
  // position + initial size in refs so React batching doesn't lose state.
  const startSplitterDrag = useCallback(
    (
      e: React.PointerEvent<HTMLDivElement>,
      dimension: 'left' | 'right' | 'timeline',
      axis: 'x' | 'y',
      direction: 1 | -1,
    ) => {
      e.preventDefault()
      e.stopPropagation()
      const startCoord = axis === 'x' ? e.clientX : e.clientY
      const startSize = panelSizes[dimension]
      const clamp = PANEL_CLAMPS[dimension]
      const handleMove = (event: PointerEvent) => {
        const coord = axis === 'x' ? event.clientX : event.clientY
        const delta = (coord - startCoord) * direction
        const next = Math.max(clamp.min, Math.min(clamp.max, startSize + delta))
        setPanelSizes((prev) => (prev[dimension] === next ? prev : { ...prev, [dimension]: next }))
      }
      const handleUp = () => {
        document.removeEventListener('pointermove', handleMove)
        document.removeEventListener('pointerup', handleUp)
        layoutLog({
          event: 'divider-released',
          divider: dimension,
          startSize,
          // setPanelSizes is async — we log via a microtask to capture the
          // committed value.
        })
      }
      document.addEventListener('pointermove', handleMove)
      document.addEventListener('pointerup', handleUp)
      layoutLog({ event: 'divider-grabbed', divider: dimension, axis, startSize })
    },
    [panelSizes, layoutLog],
  )

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="timeline-modal-backdrop"
      // Backdrop dismissal removed: timeline editor is a dedicated workspace.
      // Use the X button or Escape to close.
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          timelineLog({
            event: 'outside-click-blocked',
            nodeId,
            isRenderActive,
          })
        }
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="relative bg-card rounded-lg shadow-2xl overflow-hidden"
        data-testid="timeline-modal-shell"
        style={{
          width: '90vw',
          height: '90vh',
          minWidth: '800px',
          minHeight: '600px',
          display: 'grid',
          // RFC-0651 #5: state-backed sizes with drag-resizable dividers.
          // Columns: [left rail] [splitter] [preview] [splitter] [right rail]
          // Rows:    [toolbar] [main] [splitter] [timeline]
          gridTemplateColumns: `${panelSizes.left}px 6px 1fr 6px ${panelSizes.right}px`,
          gridTemplateRows: `auto 1fr 6px ${panelSizes.timeline}px`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Row 1: Modal Header (spans all 3 columns) */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-border bg-card-hover"
          style={{ gridColumn: '1 / -1' }}
        >
          <h2 className="text-lg font-semibold text-foreground">Timeline Editor</h2>
          <div className="flex items-center gap-3">
            {/* Export Button */}
            <button
              onClick={handleOpenExportSheet}
              disabled={isRenderActive}
              className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
                isRenderActive
                  ? 'bg-warning/20 text-warning cursor-wait'
                  : 'bg-amber-500 hover:bg-amber-400 text-black'
              }`}
              data-testid="timeline-modal-export-button"
            >
              {isRenderActive ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Rendering...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Export to Download
                </>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={handleClose}
              data-testid="timeline-modal-close-button"
              className={`p-2 rounded-lg transition-colors ${
                'text-text-muted hover:text-foreground hover:bg-surface-2'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 2, Col 1: ClipPanel (left) */}
        <div className="border-r border-border overflow-hidden">
          <ClipPanel
            clips={timelineState.clips}
            audioClips={timelineState.audioClips}
            referenceVideoClips={videoClips}
            referenceAudioClips={audioClipRefs}
            selectedClipIds={timelineState.selectedClipIds}
            onSelectClip={selectClip}
            onScrollToClip={handleScrollToClip}
            onAddClipToTimeline={handleAddClipToTimeline}
            onOpenReferenceSelector={onOpenReferenceSelector}
          />
        </div>

        {/* Row 2, Col 2: vertical splitter between left rail and preview */}
        <div
          className="bg-border hover:bg-primary/60 transition-colors"
          style={{ cursor: 'col-resize' }}
          onPointerDown={(e) => startSplitterDrag(e, 'left', 'x', 1)}
          data-testid="timeline-modal-splitter-left"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize left rail"
        />

        {/* Row 2, Col 3: VideoPreviewPlayer (center) */}
        <div className="flex flex-col bg-canvas p-4 min-h-0">
          <div className="flex-1 min-h-0">
            <VideoPreviewPlayer
              clips={timelineState.clips}
              playheadPosition={timelineState.playheadPosition}
              isPlaying={timelineState.isPlaying}
              onVideoEnd={() => togglePlayPause()}
              preloader={preloader}
              mutedVideoTracks={timelineState.mutedVideoTracks}
            />
          </div>
          {/* Playback Controls */}
          <div className="flex-shrink-0 flex items-center justify-center gap-4 mt-2">
            <button
              onClick={jumpToStart}
              disabled={timelineState.playheadPosition === 0}
              className="p-2 rounded-lg bg-surface-1 hover:bg-surface-2 text-text-secondary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Jump to start (Home)"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
              </svg>
            </button>
            <button
              onClick={handleTogglePlayPause}
              disabled={timelineState.clips.length === 0}
              className="p-3 rounded-lg bg-surface-2 hover:bg-white/15 text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Play/Pause (Space)"
              data-testid="timeline-play-pause-button"
            >
              {timelineState.isPlaying ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <button
              onClick={jumpToEnd}
              disabled={timelineState.playheadPosition >= timelineState.totalDuration}
              className="p-2 rounded-lg bg-surface-1 hover:bg-surface-2 text-text-secondary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Jump to end (End)"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 2, Col 4: vertical splitter between preview and right rail */}
        <div
          className="bg-border hover:bg-primary/60 transition-colors"
          style={{ cursor: 'col-resize' }}
          onPointerDown={(e) => startSplitterDrag(e, 'right', 'x', -1)}
          data-testid="timeline-modal-splitter-right"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize right rail"
        />

        {/* Row 2, Col 5: AdjustPanel (right) */}
        <div className="border-l border-border overflow-hidden">
          <AdjustPanel
            selectedVideoClip={selectedVideoClip}
            selectedAudioClip={selectedAudioClip}
            selectedClipType={
              isMultiSelected
                ? 'mixed'
                : (selectionSummary.type === 'video' || selectionSummary.type === 'audio'
                    ? selectionSummary.type
                    : null)
            }
            multiSelectionCount={timelineState.selectedClipIds.size}
            onSpeedChange={handleSpeedChange}
            onAudioVolumeChange={setAudioVolume}
            onAudioToggleMute={toggleAudioMute}
            onClipVolumeChange={setClipVolume}
            onClipToggleMute={toggleClipMute}
            onClipFadeOutChange={setClipFadeOut}
            onClipFadeInChange={setClipFadeIn}
            onExtractAudioFromClip={handleExtractAudio}
          />
        </div>

        {/* Row 3: horizontal splitter between preview area and timeline */}
        <div
          className="bg-border hover:bg-primary/60 transition-colors"
          style={{ cursor: 'row-resize', gridColumn: '1 / -1' }}
          onPointerDown={(e) => startSplitterDrag(e, 'timeline', 'y', -1)}
          data-testid="timeline-modal-splitter-timeline"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize timeline"
        />

        {/* Row 4: TimelineEditor (bottom, spans all 5 columns) */}
        <div className="border-t border-border" style={{ gridColumn: '1 / -1' }}>
          <TimelineEditor
            timelineState={timelineState}
            onSelectClip={selectClip}
            onToggleClipSelection={toggleClipSelection}
            onClearSelection={clearSelection}
            onMultiDragOffset={handleMultiDragCommit}
            onRippleDelete={closeAllGaps}
            onSetPlayhead={setPlayhead}
            onSetZoom={setZoom}
            onReorderClip={handleReorderClipCommit}
            onClipOffsetChange={handleSetClipOffsetCommit}
            onClipTrackIndexChange={setClipTrackIndex}
            onTrimClip={handleTrimClipCommit}
            onTrimClipPreview={trimClipPreview}
            onNormalizeTimelineOrigin={normalizeTimelineOrigin}
            onAudioOffsetChange={setAudioOffset}
            onAudioTrimClip={trimAudioClip}
            onAudioTrimClipPreview={trimAudioClipPreview}
            onAudioTrackIndexChange={setAudioTrackIndex}
            onAddTrack={(type) => {
              timelineLog({
                event: 'track-add',
                nodeId,
                trackType: type,
                videoTrackCount: timelineState.videoTrackCount,
                audioTrackCount: timelineState.audioTrackCount,
              })
              if (type === 'video') {
                addVideoTrack()
              } else {
                addAudioTrack()
              }
            }}
            onRemoveTrack={(trackKind, trackIndex, deleteClips) => {
              if (trackKind === 'video') {
                removeVideoTrack(trackIndex, deleteClips)
              } else {
                removeAudioTrack(trackIndex, deleteClips)
              }
            }}
            onDropReference={handleAddReferenceAt}
            playheadPositionRef={playheadPositionRef}
            readOnly={isRenderActive}
            onSplitSelectedClip={handleSplitSelected}
            onDeleteSelectedClip={deleteSelectedClip}
            onCopyClip={copyClip}
            onPasteClip={pasteClip}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            magnetEnabled={timelineState.magnetEnabled}
            onSetMagnetEnabled={setMagnetEnabled}
            onMagnetPackTrack0={magnetPackTrack0}
            mutedVideoTracks={timelineState.mutedVideoTracks}
            mutedAudioTracks={timelineState.mutedAudioTracks}
            onToggleTrackMute={toggleTrackMute}
          />
        </div>

        {/* Render Progress Overlay */}
        {isRenderActive && (
          <div
            className="absolute inset-0 bg-black/50 flex items-center justify-center z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-card rounded-xl p-8 flex flex-col items-center gap-4 max-w-sm">
              <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
              <div className="text-sm text-text-secondary text-center">
                {renderMessage || 'Rendering in the background. It is safe to go back to canvas.'}
              </div>
              {renderProgress > 0 && (
                <div className="w-full">
                  <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-300 ease-out"
                      style={{ width: `${renderProgress}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mt-2">
                <button
                  onClick={onClose}
                  className="h-9 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors"
                  data-testid="render-overlay-go-back"
                >
                  Go back to canvas
                </button>
                <button
                  onClick={handleCancelRender}
                  disabled={isCancelling}
                  className="h-9 px-4 rounded-lg border border-destructive/50 bg-destructive/10 hover:bg-destructive/20 text-destructive text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="render-overlay-cancel"
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ExportConfirmSheet
          isOpen={isExportSheetOpen}
          defaultName={exportSheetDefaultName}
          existingLabels={existingLabels}
          onCancel={handleCancelExportSheet}
          onConfirm={handleConfirmExport}
        />
      </div>
    </div>,
    document.body
  )
}

/**
 * RFC-0651 #12: shape of the response from `POST /api/projects/:id/assets`
 * — the endpoint historically returned either `path` or `relpath` on
 * success, depending on which storage backend handled the write (Supabase
 * vs local-fs; both have since been replaced by the GCS-FUSE-backed
 * fs_storage shim, but the dual response shape lingered).
 */
interface AssetUploadResponse {
  path?: string
  relpath?: string
}

/**
 * RFC-0651 #12: minimal 16-bit PCM WAV encoder. Takes per-channel Float32
 * arrays and the source sample rate; returns a Blob ready for upload.
 * Mono/stereo only; deliberately small and dependency-free.
 */
function encodeWavBlob(channels: Float32Array[], sampleRate: number): Blob {
  const numChannels = channels.length
  const numSamples = channels[0]?.length ?? 0
  const bytesPerSample = 2
  const dataSize = numSamples * numChannels * bytesPerSample
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i))
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true)
  view.setUint16(32, numChannels * bytesPerSample, true)
  view.setUint16(34, 16, true)
  writeString(36, 'data')
  view.setUint32(40, dataSize, true)

  let offset = 44
  for (let i = 0; i < numSamples; i += 1) {
    for (let ch = 0; ch < numChannels; ch += 1) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]))
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
      offset += 2
    }
  }
  return new Blob([buffer], { type: 'audio/wav' })
}
